import { toast } from 'react-toastify';

import { Address } from 'viem';

import { AsyncTransactionResult, LaunchpadSalePoolType } from '@api';

import { DEFAULT_TARGET_CHAIN, namedWeb3Errors } from '@shared/constants';
import { normalizeError } from '@shared/helpers/displayErrorToast';
import displayWalletErrorToast, {
  WALLET_ERROR_TOAST_ACTION_SCOPE,
} from '@shared/helpers/displayWalletErrorToast';
import clearSavedConnectionDetails from '@shared/helpers/web3/clearSavedConnectionDetails';
import { handleTransactionResult } from '@shared/helpers/web3/handleTransactionResult';
import launchAsyncToast from '@shared/helpers/web3/launchAsyncToast';
import useCheckDepositAllowance from '@shared/hooks/web3/useCheckDepositAllowance';
import useIncreaseDepositAllowance from '@shared/hooks/web3/useIncreaseDepositAllowance';
import useWeb3 from '@shared/hooks/web3/useWeb3';

import useLaunchpadUserWalletInfo from '@launchpad/hooks/useLaunchpadUserWalletInfo';
import depositContractCall from '@launchpad/utils/contract';

import { getDepositToken, getDepositTokenInfo } from './useDepositTokenQuery';

const useDeposit = ({ type }: { type: LaunchpadSalePoolType }) => {
  const { connectWallet } = useWeb3();
  const { getUserWalletType } = useLaunchpadUserWalletInfo();
  const { checkIsEnoughDepositAllowance } = useCheckDepositAllowance();
  const { increaseDepositAllowance } = useIncreaseDepositAllowance();

  const verifyTheDepositAllowance = async (amount: number, contractAddress: Address) => {
    const tokenAddress = await getDepositToken({
      type,
      depositContractAddress: contractAddress,
    });

    const depositTokenInfo = getDepositTokenInfo(tokenAddress);
    // Step 2.1: Verify the deposit allowance.
    const isEnoughAllowance = await checkIsEnoughDepositAllowance({
      targetChain: DEFAULT_TARGET_CHAIN,
      amount,
      spenderAddress: contractAddress,
      contractAddress: depositTokenInfo.address,
      token: depositTokenInfo,
    });

    if (isEnoughAllowance === undefined) {
      return;
    }

    // Step 2.2: Increase deposit allowance if not sufficient.
    if (!isEnoughAllowance) {
      const increasingTransaction = await increaseDepositAllowance({
        targetChain: DEFAULT_TARGET_CHAIN,
        amount,
        spenderAddress: contractAddress,
        contractAddress: depositTokenInfo.address,
        token: depositTokenInfo,
      });

      if (!increasingTransaction) {
        return;
      }

      // Wait for the transaction to complete before do the next step.
      await handleTransactionResult(increasingTransaction);

      // Check the allowance again due to it could be changed in the wallet on runtime
      await verifyTheDepositAllowance(amount, contractAddress);
    }
  };

  const deposit = async (props: {
    amount: number;
    contractAddress: Address;
    amountInWei: string;
    exchangeRateInWei: string;
    boosted: boolean;
    userId: string;
    transactionId: string;
    walletAddress: Address;
    timeStampLimit: number;
    signature: Address;
  }): Promise<AsyncTransactionResult> => {
    const toastId = Date.now();
    const { amount, contractAddress } = props;

    try {
      if (!contractAddress) {
        throw new Error('Contract address is not provided');
      }

      const { walletType, address } = getUserWalletType();

      // Verify and possibly increase the deposit allowance.
      try {
        await verifyTheDepositAllowance(amount, contractAddress);
      } catch (error: any) {
        if (error?.message) {
          console.error(error.message);
        }

        displayWalletErrorToast({
          error,
          unknownExceptionReplaceMessage: namedWeb3Errors.UNKNOWN_TRANSACTION_ERROR,
          details: { scope: WALLET_ERROR_TOAST_ACTION_SCOPE.UNPAID_GAS_FEES },
        });
        return;
      }

      const writeContractResult = await depositContractCall({ type, props });

      return (await Promise.race([
        writeContractResult,
        launchAsyncToast({
          walletType,
          address,
          toastId,
          hideRetryButton: true,
          title: 'Depositing...',
        }),
      ])) as Awaited<AsyncTransactionResult>;
    } catch (error) {
      const normalizedError = normalizeError(error);

      const reconnectAndRetry = async () => {
        const { walletType, address: targetAddress } = getUserWalletType();

        await connectWallet({
          walletType,
          targetAddress,
        });

        return deposit(props);
      };

      if (normalizedError.message === namedWeb3Errors.CONNECTOR_NOT_FOUND) {
        clearSavedConnectionDetails();
        return await reconnectAndRetry();
      }

      if (normalizedError.message === namedWeb3Errors.RETRY) {
        return await deposit(props);
      }

      throw error;
    } finally {
      toast.dismiss(toastId);
    }
  };

  return { deposit };
};

export default useDeposit;
